import { Button, Input, Label, PaguzLogo } from '@paguz/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { normalizePhone, startOtp, verifyOtp } from '../../api/auth.ts';
import { customerMessage, isApiError } from '../../api/errors.ts';
import { queryKeys } from '../../api/query-client.ts';
import { InlineAlert } from '../../components/feedback.tsx';
import { config } from '../../config.ts';
import { OtpStep } from './otp-step.tsx';

/** Personas sembradas (`docs/03-demo/02-datos-semilla.md`); solo se muestran en modo demo. */
const DEMO_PERSONAS = [
  { name: 'Rosa', state: 'con cupo', phone: '300 111 2233' },
  { name: 'Jairo', state: 'sin crédito', phone: '310 222 3344' },
  { name: 'Hernán', state: 'en mora', phone: '312 444 5566' },
] as const;

interface Challenge {
  phone: string;
  challengeId: string;
  demoCode: string | undefined;
}

/** Ingreso con celular + código OTP. */
export function LoginPage({ redirectTo }: { redirectTo: string | undefined }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [phoneInput, setPhoneInput] = useState('');
  const [error, setError] = useState<{ message: string; notRegistered: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState<Challenge | null>(null);

  async function requestCode(phone: string) {
    const result = await startOtp(phone, 'LOGIN');
    setChallenge({ phone, challengeId: result.challengeId, demoCode: result.demoCode });
  }

  async function onSubmitPhone() {
    const phone = normalizePhone(phoneInput);
    if (phone === null) {
      setError({
        message: 'Escribe tu celular de 10 dígitos, por ejemplo 300 111 2233.',
        notRegistered: false,
      });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await requestCode(phone);
    } catch (err) {
      setError({
        message: customerMessage(err),
        notRegistered: isApiError(err) && err.reasonCode === 'PZ-CUS-404',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-1 flex-col px-4 pt-10 pb-8">
      <PaguzLogo className="mb-8 h-9 w-auto self-start" />
      <h1 className="mb-2 text-3xl">{challenge ? 'Revisa tu celular' : 'Ingresa a tu cuenta'}</h1>
      {challenge === null ? (
        <form
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            void onSubmitPhone();
          }}
          noValidate
        >
          <p className="text-muted-foreground">
            Compra a crédito con QR Bre-B y paga en cuotas. Te enviaremos un código por SMS.
          </p>
          <div className="flex flex-col gap-2">
            <Label htmlFor="phone">Tu número de celular</Label>
            <Input
              id="phone"
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              placeholder="300 111 2233"
              value={phoneInput}
              onChange={(event) => {
                setPhoneInput(event.target.value);
              }}
              aria-invalid={error !== null || undefined}
              aria-describedby={error ? 'phone-error' : undefined}
            />
          </div>
          {error && (
            <InlineAlert tone="danger" className="-mt-2">
              <span id="phone-error">{error.message}</span>
              {error.notRegistered && (
                <>
                  {' '}
                  <Link to="/registro" className="font-semibold underline">
                    Regístrate aquí
                  </Link>
                </>
              )}
            </InlineAlert>
          )}
          <Button type="submit" size="lg" loading={busy}>
            Recibir código
          </Button>
          {config.demoMode && (
            <InlineAlert tone="info" title="Personas de demostración">
              <ul className="mt-1 flex flex-col gap-1">
                {DEMO_PERSONAS.map((persona) => (
                  <li key={persona.phone}>
                    <button
                      type="button"
                      className="min-h-8 text-left underline-offset-2 hover:underline"
                      onClick={() => {
                        setPhoneInput(persona.phone);
                        setError(null);
                      }}
                    >
                      <strong>{persona.name}</strong> ({persona.state}): {persona.phone}
                    </button>
                  </li>
                ))}
              </ul>
            </InlineAlert>
          )}
          <p className="text-center text-muted-foreground">
            ¿Es tu primera vez?{' '}
            <Link to="/registro" className="font-semibold text-primary underline">
              Crea tu cuenta
            </Link>
          </p>
        </form>
      ) : (
        <OtpStep
          phone={challenge.phone}
          demoCode={challenge.demoCode}
          onChangePhone={() => {
            setChallenge(null);
          }}
          onResend={() => requestCode(challenge.phone)}
          onVerify={async (code) => {
            const customer = await verifyOtp({ challengeId: challenge.challengeId, code });
            queryClient.setQueryData(queryKeys.me, customer);
            await navigate({ href: redirectTo ?? '/', replace: true });
          }}
        />
      )}
    </div>
  );
}
